import React, { useEffect, useRef, useCallback, useState } from 'react'
import Quill from 'quill'
import 'quill/dist/quill.snow.css'
import 'quill-mention/dist/quill.mention.css'
import { Mention, MentionBlot } from 'quill-mention'
import './index.css'
import { Row } from 'antd'
import { SendOutlined } from '@ant-design/icons'
import Button from '../button/button'

Quill.register('modules/mention', Mention)
Quill.register('formats/mention', MentionBlot)

const MentionEditor = ({
    member = [],
    handleChangeMentions,
    value,
    clearTrigger,
    loadingButton,
    onClickComment,
    disabledButton,
}) => {
    const editorRef = useRef(null)
    const quillRef = useRef(null)
    const containerRef = useRef(null)
    const [showButton, setShowButton] = useState(false)

    const mentionValues = member.map((user) => ({
        id: user?.id,
        value: user?.display,
        avatar: user?.profilePicture,
    }))

    const renderMentionItem = useCallback((item) => {
        const container = document.createElement('div')
        container.className = 'mention-item'

        const avatarWrapper = document.createElement('div')
        avatarWrapper.className = 'mention-avatar'

        if (item?.avatar) {
            const img = document.createElement('img')
            img.src = item?.avatar
            img.alt = item?.value
            img.className = 'mention-img'
            avatarWrapper.appendChild(img)
        } else {
            avatarWrapper.textContent = item?.value
                .split(' ')
                .map((n) => n[0])
                .filter(Boolean)
                .join('')
                .slice(0, 2)
                .toUpperCase()
        }

        const label = document.createElement('span')
        label.textContent = item?.value
        label.className = 'mention-label'

        container.appendChild(avatarWrapper)
        container.appendChild(label)
        return container
    }, [])

    useEffect(() => {
        if (!editorRef.current || quillRef.current || member.length === 0) return

        quillRef.current = new Quill(editorRef.current, {
            theme: 'snow',
            placeholder: 'Add a comment',
            modules: {
                toolbar: [['bold', 'italic', 'underline']],
                mention: {
                    mentionDenotationChars: ['@'],
                    isolateCharacter: true,
                    renderItem: renderMentionItem,
                    source: (searchTerm, renderList) => {
                        const matches = mentionValues.filter((item) =>
                            item.value
                                .toLowerCase()
                                .includes(searchTerm.toLowerCase())
                        )
                        renderList(matches, searchTerm)
                    },
                },
            },
        })

        if (value && typeof value === 'string') {
            quillRef.current.clipboard.dangerouslyPasteHTML(value)
        }

        quillRef.current.on('text-change', () => {
            const html =
                editorRef.current.querySelector('.ql-editor')?.innerHTML || ''
            const delta = quillRef.current.getContents()
            const mentions = []
            let plainText = ''

            delta.ops?.forEach((op) => {
                if (typeof op.insert === 'string') {
                    plainText += op.insert
                } else if (op.insert?.mention) {
                    mentions.push(op.insert.mention.id)
                    plainText += `@${op.insert.mention.value} `
                }
            })

            handleChangeMentions(plainText.trim(), html, [...new Set(mentions)])

            // 👇 toggle button only if user typed something
            const hasContent =
                quillRef.current.getText().trim().length > 0 ||
                mentions.length > 0
            setShowButton(hasContent)
        })
    }, [member, value, handleChangeMentions, mentionValues, renderMentionItem])

    // external clear trigger
    useEffect(() => {
        if (clearTrigger && quillRef.current) {
            quillRef.current.setText('')
            setShowButton(false)
        }
    }, [clearTrigger])

    const handleFocus = () => {
        containerRef.current?.classList.add('focused')
    }
    const handleBlur = () => {
        containerRef.current?.classList.remove('focused')
    }

    // 👇 Clear editor on submit
    const handleSubmit = () => {
        onClickComment()
        if (quillRef.current) {
            quillRef.current.setText('') // clear text
            setShowButton(false) // hide button again
        }
    }

    return (
        <div ref={containerRef} className="mention-editor-container">

            <div
                ref={editorRef}
                className="mention-editor"
                onFocus={handleFocus}
                onBlur={handleBlur}
            />
            {showButton && (
                <div style={{ justifyContent: "end", display: "flex", alignItems: "center", marginRight: "5px", marginBottom: "5px" }} className="mention-action">
                    <Button
                        props={{
                            icon: <SendOutlined />,
                            style: {
                                margin: 0,
                                height: '40px',
                                width: '40px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                alignItems: "center"
                            },
                            loadingButton: loadingButton,
                            onClick: handleSubmit,
                            disabled: disabledButton,
                        }}
                    />
                </div>
            )}
        </div>
    )
}

export default MentionEditor
